/**
 * Modal — the ONE shared modal shell behind both nested-child and wide-grouped
 * input forms (§21a). HeaderChildModal and WideGroupedModal are thin
 * compositions over this shell; they differ only in the body content
 * (a `.mform` + child grid vs `.subhead`-divided `.mform.three` sections).
 *
 * Portaled to <body>, backdrop-click + Escape to close, `role="dialog"` +
 * `aria-modal`. The footer's primary action is caller-supplied.
 */
import { useEffect, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  /** Small context chip in the header (e.g. "Redhawk 1H · Drilling · Run 7"). */
  context?: string;
  /** Body content. */
  children: ReactNode;
  /** Footer left-aligned helper text. */
  footerNote?: ReactNode;
  /** Footer action buttons (right-aligned). Caller supplies Cancel/Save. */
  footer?: ReactNode;
  /** Max width in px. Defaults to 620 (wide grouped modals pass ~940). */
  width?: number;
  /** Accessible label; defaults to `title`. */
  ariaLabel?: string;
}

export function Modal({
  open,
  onClose,
  title,
  context,
  children,
  footerNote,
  footer,
  width = 620,
  ariaLabel,
}: ModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    // Move focus into the dialog for keyboard users.
    dialogRef.current?.focus();
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  if (!open || typeof document === "undefined") return null;

  return createPortal(
    <div
      className="modal-scrim"
      onClick={(e) => {
        // Backdrop click closes; clicks inside the dialog do not bubble here.
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel ?? title}
        tabIndex={-1}
        style={{ width: `min(${width}px, 100%)` }}
      >
        <div className="mhead">
          <h2>{title}</h2>
          {context ? <span className="mctx">{context}</span> : null}
          <button type="button" className="x" aria-label="Close" onClick={onClose}>
            &times;
          </button>
        </div>
        <div className="mbody">{children}</div>
        {footer || footerNote ? (
          <div className="mfoot">
            {footerNote ? <span className="l">{footerNote}</span> : null}
            {footer}
          </div>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}
